export default {
  en: {
    title: "Channels",
    subtitle: "Where people reach your agents (website chat, chat apps, e-mail, voice), and how staff take over a conversation.",
    tab: { desk: "Desk", settings: "Settings", widgets: "Website chat", voice: "Voice", connect: "Connect apps", notify: "Send a notification", consents: "Consents" },
    cancel: "Cancel",
    channel: { web: "Web", api: "API", widget: "Website widget", email: "Email", chat: "Chat app", voice: "Voice" },
    lang: { ar: "Arabic", en: "English" },
    desk: {
      intro: "Conversations an agent handed over to a person, oldest first. Reply here, then hand the conversation back to the agent or close it.",
      list: "Waiting conversations",
      none: "No conversation is waiting for a person.",
      waiting: "Waiting since {{at}}",
      participant: { user: "Signed-in user", visitor: "Website visitor", external: "External contact" },
      pick: "Choose a conversation to read and answer it.",
      conversation: "Conversation",
      sender: { participant: "Customer", agent: "Agent", human_agent: "Staff", system: "System" },
      untrusted: "Untrusted input",
      reply: "Your reply",
      send: "Send reply",
      sent: "Reply sent",
      resume: "Hand back to the agent",
      resumed: "Conversation handed back to the agent",
      close: "Close conversation",
      closeConfirm: "Close this conversation? The customer can start a new one.",
      closed: "Conversation closed",
      open: "Open conversation {{id}}",
    },
    settings: {
      intro: "Applies to every channel of your organisation. Only channel admins can change it.",
      readOnly: "Read-only: changing channel settings needs the channel-admin role.",
      timezone: "Time zone",
      quietStart: "Quiet hours start (HH:MM)",
      quietEnd: "Quiet hours end (HH:MM)",
      weekendDays: "Weekend days",
      days: { "1": "Monday", "2": "Tuesday", "3": "Wednesday", "4": "Thursday", "5": "Friday", "6": "Saturday", "7": "Sunday" },
      defaultLanguage: "Default language",
      digits: "Digits",
      digitsLatin: "Latin (0-9)",
      digitsArabic: "Arabic-Indic (٠-٩)",
      perHour: "Outbound messages per hour",
      perRecipient: "Outbound messages per recipient per day",
      optOut: "Opt-out keywords",
      queueRoles: "Roles of the human queue",
      sla: "Handover SLA (seconds)",
      defaultAgent: "Default agent",
      noAgent: "None",
      save: "Save settings",
      saved: "Channel settings saved",
    },
    widgets: {
      intro: "Create an embeddable chat widget for a website. The embed key is shown once; store it in the website's configuration.",
      name: "Widget name",
      origins: "Allowed website origins",
      originsHelp: "Like https://www.example.com — scheme and host, optional port.",
      agent: "Agent",
      language: "Default language",
      accent: "Accent colour",
      position: "Position",
      positions: { "bottom-end": "Bottom, end side", "bottom-start": "Bottom, start side" },
      create: "Create widget",
      created: "Widget {{name}} created",
      createdTitle: "Widgets created in this session",
      embedKey: "Embed key (version {{v}})",
      snippet: "Embed snippet",
      copy: "Copy",
      copyKey: "Copy the embed key of {{name}}",
      copySnippet: "Copy the snippet of {{name}}",
      copied: "Copied",
    },
    consents: {
      intro: "Consent is required before any outbound message. Recipients are stored as hashes; look them up by channel and address.",
      channel: "Channel",
      address: "Address (e-mail, phone or chat ID)",
      lookup: "Look up",
      none: "No consent recorded for this recipient.",
      purpose: "Purpose",
      evidence: "Evidence reference",
      evidenceHelp: "Signed form ID, recorded call ID or web consent receipt.",
      record: "Record consent",
      recorded: "Consent recorded",
      granted: "Granted",
      grantedBy: "Granted by",
      status: "Status",
      active: "Active",
      revokedAt: "Revoked {{at}}",
      recipient: "Recipient (SHA-256)",
      revoke: "Revoke",
      revokeAria: "Revoke consent for {{purpose}}",
      revokeTitle: "Revoke consent for {{purpose}}",
      revokeBody: "No further outbound messages for this purpose will be sent to the recipient.",
      reason: "Reason (at least 3 characters)",
      revoked: "Consent revoked",
      actions: "Actions",
    },
    issues: {
      origins_required: "Add at least one website origin",
      origin_invalid: "Origins look like https://host or https://host:port",
      agent_required: "Choose an agent",
      name_required: "Enter a name",
      time_invalid: "Times use HH:MM",
      queue_required: "Keep at least one queue role",
      purpose_invalid: "Purpose: lower-case letters, digits, '.', '_' or '-', starting with a letter",
      evidence_short: "Evidence needs at least 3 characters",
      address_short: "Address needs at least 3 characters",
      reason_short: "The reason needs at least 3 characters",
      text_required: "Write a reply",
      purpose_required: "Choose the kind of message",
    },
  },
  ar: {
    title: "القنوات",
    subtitle: "أين يصل الناس إلى وكلائك (محادثة الموقع وتطبيقات المحادثة والبريد والصوت)، وكيف يتولى الموظفون المحادثة.",
    tab: { desk: "المكتب", settings: "الإعدادات", widgets: "محادثة الموقع", voice: "الصوت", connect: "ربط التطبيقات", notify: "إرسال إشعار", consents: "الموافقات" },
    cancel: "إلغاء",
    channel: { web: "الويب", api: "واجهة برمجية", widget: "أداة الموقع", email: "البريد الإلكتروني", chat: "تطبيق محادثة", voice: "صوت" },
    lang: { ar: "العربية", en: "الإنجليزية" },
    desk: {
      intro: "المحادثات التي حوّلها الوكيل إلى موظف، الأقدم أولًا. ردّ هنا، ثم أعد المحادثة إلى الوكيل أو أغلقها.",
      list: "المحادثات المنتظرة",
      none: "لا توجد محادثة بانتظار موظف.",
      waiting: "بانتظار منذ {{at}}",
      participant: { user: "مستخدم مسجّل", visitor: "زائر الموقع", external: "جهة اتصال خارجية" },
      pick: "اختر محادثة لقراءتها والرد عليها.",
      conversation: "المحادثة",
      sender: { participant: "العميل", agent: "الوكيل", human_agent: "الموظف", system: "النظام" },
      untrusted: "مُدخل غير موثوق",
      reply: "ردّك",
      send: "إرسال الرد",
      sent: "أُرسل الرد",
      resume: "إعادة المحادثة إلى الوكيل",
      resumed: "أُعيدت المحادثة إلى الوكيل",
      close: "إغلاق المحادثة",
      closeConfirm: "إغلاق هذه المحادثة؟ يمكن للعميل بدء محادثة جديدة.",
      closed: "أُغلقت المحادثة",
      open: "فتح المحادثة {{id}}",
    },
    settings: {
      intro: "تنطبق على كل قنوات جهتك. لا يغيّرها إلا مسؤولو القنوات.",
      readOnly: "للقراءة فقط: تغيير إعدادات القنوات يتطلب دور مسؤول القنوات.",
      timezone: "المنطقة الزمنية",
      quietStart: "بداية ساعات الهدوء (س:د)",
      quietEnd: "نهاية ساعات الهدوء (س:د)",
      weekendDays: "أيام نهاية الأسبوع",
      days: { "1": "الاثنين", "2": "الثلاثاء", "3": "الأربعاء", "4": "الخميس", "5": "الجمعة", "6": "السبت", "7": "الأحد" },
      defaultLanguage: "اللغة الافتراضية",
      digits: "الأرقام",
      digitsLatin: "لاتينية (0-9)",
      digitsArabic: "عربية هندية (٠-٩)",
      perHour: "الرسائل الصادرة في الساعة",
      perRecipient: "الرسائل الصادرة لكل مستلم يوميًا",
      optOut: "كلمات إلغاء الاشتراك",
      queueRoles: "أدوار قائمة الموظفين",
      sla: "مهلة التحويل (بالثواني)",
      defaultAgent: "الوكيل الافتراضي",
      noAgent: "لا يوجد",
      save: "حفظ الإعدادات",
      saved: "حُفظت إعدادات القنوات",
    },
    widgets: {
      intro: "أنشئ أداة دردشة قابلة للتضمين في موقع إلكتروني. يُعرض مفتاح التضمين مرة واحدة؛ احفظه في إعدادات الموقع.",
      name: "اسم الأداة",
      origins: "أصول المواقع المسموح بها",
      originsHelp: "مثل https://www.example.com — البروتوكول والمضيف ومنفذ اختياري.",
      agent: "الوكيل",
      language: "اللغة الافتراضية",
      accent: "لون التمييز",
      position: "الموضع",
      positions: { "bottom-end": "أسفل، جهة النهاية", "bottom-start": "أسفل، جهة البداية" },
      create: "إنشاء الأداة",
      created: "أُنشئت الأداة {{name}}",
      createdTitle: "الأدوات المنشأة في هذه الجلسة",
      embedKey: "مفتاح التضمين (الإصدار {{v}})",
      snippet: "شيفرة التضمين",
      copy: "نسخ",
      copyKey: "نسخ مفتاح تضمين {{name}}",
      copySnippet: "نسخ شيفرة {{name}}",
      copied: "تم النسخ",
    },
    consents: {
      intro: "الموافقة مطلوبة قبل أي رسالة صادرة. تُخزَّن المستلمون كبصمات؛ ابحث عنهم بالقناة والعنوان.",
      channel: "القناة",
      address: "العنوان (بريد إلكتروني أو هاتف أو معرّف محادثة)",
      lookup: "بحث",
      none: "لا توجد موافقة مسجلة لهذا المستلم.",
      purpose: "الغرض",
      evidence: "مرجع الإثبات",
      evidenceHelp: "معرّف نموذج موقّع أو مكالمة مسجلة أو إيصال موافقة إلكتروني.",
      record: "تسجيل الموافقة",
      recorded: "سُجّلت الموافقة",
      granted: "تاريخ المنح",
      grantedBy: "منحها",
      status: "الحالة",
      active: "سارية",
      revokedAt: "أُلغيت في {{at}}",
      recipient: "المستلم (SHA-256)",
      revoke: "سحب",
      revokeAria: "سحب الموافقة على {{purpose}}",
      revokeTitle: "سحب الموافقة على {{purpose}}",
      revokeBody: "لن تُرسل بعد الآن رسائل صادرة لهذا الغرض إلى المستلم.",
      reason: "السبب (3 أحرف على الأقل)",
      revoked: "سُحبت الموافقة",
      actions: "الإجراءات",
    },
    issues: {
      origins_required: "أضف أصل موقع واحدًا على الأقل",
      origin_invalid: "الأصل بالشكل https://host أو https://host:port",
      agent_required: "اختر وكيلًا",
      name_required: "أدخل اسمًا",
      time_invalid: "الوقت بالشكل س:د (HH:MM)",
      queue_required: "أبقِ دورًا واحدًا على الأقل في القائمة",
      purpose_invalid: "الغرض: أحرف لاتينية صغيرة وأرقام و'.' و'_' و'-'، ويبدأ بحرف",
      evidence_short: "يحتاج الإثبات إلى 3 أحرف على الأقل",
      address_short: "يحتاج العنوان إلى 3 أحرف على الأقل",
      reason_short: "يحتاج السبب إلى 3 أحرف على الأقل",
      text_required: "اكتب ردًا",
      purpose_required: "اختر نوع الرسالة",
    },
  },
};
